import React, { useEffect, useRef, useState, useMemo } from 'react';
import envConfig from '@nile/home/src/Config/env';
import styled from 'styled-components';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  BackgroundIcon,
  ChatThreads,
  withErrorBoundary,
  NewSideConversation,
  Loader,
  COLOR_MAP,
  EventEmitter
} from '@nile/components';
import {
  CREATE_VENDOR_CONVERSATION,
  GET_VENDOR_CONVERSATION_LIST,
  UPDATE_VENDORCONVERSATION_READ_STATUS
} from '@nile/graphql';
import { useGlobalChatHook } from '@nile/components/src/Components/GlobalChatHook';
import { isEqual } from 'lodash';
import { SVGIcon } from '@superops/super-icons';
import { Button } from '@superops/ui-design-system';

let broadcastChannel;
if (BroadcastChannel) {
  broadcastChannel = new BroadcastChannel('SuperShare');
}

const EmptyState = styled.div`
  .empty-message {
    max-width: 345px;
  }
`;

const Description = styled.span`
  color: #000000;
  opacity: 0.6;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  margin: 6px auto;
  text-align: center;
`;

const StyledConversation = styled.div`
  overflow: auto;
  align-content: start;
  height: calc(100% - 8px);

  -ms-overflow-style: none;
  ::-webkit-scrollbar {
    display: none;
  }

  scrollbar-width: none;

  .rdw-editor-wrapper {
    margin-top: 12px !important;
    margin-bottom: 12px !important;
    border: 1px solid #ececec;
  }
  .action-buttons .Polaris-Button--plain {
    margin-left: auto;
  }
`;

const VendorConversations = (props) => {
  const {
    workItemId,
    workItem,
    module,
    conversationToBeOpened,
    vendorConversations,
    vendorConversationLoading,
    refetch: RefetchConversation,
    updateFeedsCache,
    createNewSideConversation,
    emptyStateData = {},
    readOnly,
    modalOnly,
    toggleNewSideConversation,
    backgroundThreadConversation,
    templateId
  } = props;
  const [conversations, setConversations] = useState(vendorConversations || []);
  const ThreadRef = useRef();
  const [sentMessageData, setSentMessageData] = useState(null);
  const { getGlobalConversations } = useGlobalChatHook();

  const globalConversations = getGlobalConversations();

  const isNewSideConversationDisabled = useMemo(() => {
    const currentTicketConversationData = globalConversations?.find(
      (conversation) => conversation?.data?.workItem?.workId === workItemId
    );

    if (currentTicketConversationData) {
      const data = currentTicketConversationData?.data?.mode;
      return data === 'NEW_SIDE_CONVERSATION';
    }

    return false;
  }, [globalConversations, workItemId]);

  if (backgroundThreadConversation) {
    broadcastChannel.onmessage = (event) => {
      const eventData = JSON.parse(event.data);
      const { context } = eventData;
      const payload = eventData?.payload;

      if (
        payload?.type === 'SERVER_EVENT' &&
        context === 'pageUpdate' &&
        payload?.event === 'UPDATE_CONVERSATION' &&
        !payload?.showToast
      ) {
        const conversation = payload?.data?.conversation;

        if (conversation?.workItem.workId === workItemId) {
          const outgoingMailFailureReason = conversation?.outgoingMailFailureReason
            ? JSON.parse(conversation?.outgoingMailFailureReason)
            : null;
          setSentMessageData({ ...conversation, outgoingMailFailureReason });
          setTimeout(() => {
            setSentMessageData(null);
          }, 1000);
        }
      }
    };
  }

  useEffect(() => {
    if (!isEqual(vendorConversations, conversations)) {
      setConversations(vendorConversations);
    }
  }, [vendorConversations]);

  const getConverSation = () => {
    RefetchConversation();
  };

  const refetchSideConversation = (params) => {
    if (params?.workId === workItemId) {
      RefetchConversation();
    }
  };

  useEffect(() => {
    EventEmitter.subscribe('refetchSideConversation', refetchSideConversation);
    getConverSation();
    return function cleanup() {
      EventEmitter.unsubscribe('refetchSideConversation');
    };
  }, []);

  if (vendorConversationLoading) return <Loader />;

  if (conversations?.length)
    return (
      <StyledConversation
        ref={ThreadRef}
        className="fx fcol g15 full-width full-height p12 lightpurple-to-black-bg"
      >
        {conversations.map((conversation, idx) => {
          return (
            <ChatThreads
              {...props}
              isNewSideConversationDisabled={isNewSideConversationDisabled}
              key={conversation?.conversationId}
              containerRef={ThreadRef}
              conversation={conversation}
              isOpened={
                conversationToBeOpened
                  ? conversation?.conversationId === conversationToBeOpened
                  : idx === 0
              }
              conversationToBeOpened={conversationToBeOpened}
              clientId={workItem?.client?.accountId || null}
              query={GET_VENDOR_CONVERSATION_LIST}
              responseKey="getVendorConversationsList"
              condition={{
                attribute: 'workItem.workId',
                value: workItemId
              }}
              refetchParent={RefetchConversation}
              createConversationQuery={CREATE_VENDOR_CONVERSATION}
              updateReadStatusApi={UPDATE_VENDORCONVERSATION_READ_STATUS}
              updateCache={updateFeedsCache}
              createNewSideConversation={createNewSideConversation}
              readOnly={readOnly}
              modalOnly={modalOnly}
              toggleNewSideConversation={toggleNewSideConversation}
              backgroundThreadConversation={backgroundThreadConversation}
              sentMessageData={sentMessageData}
            />
          );
        })}
      </StyledConversation>
    );

  return (
    <StyledConversation className="fx fcol g15 full-width full-height p12 lightpurple-to-black-bg">
      <EmptyState className="full-width full-height flex actr jctr">
        <div className="fx col actr g15">
          {/* Illustration images hence not changed to SVG */}
          <BackgroundIcon
            url={`url(${envConfig.imageRoot}svg/noExternalConversations.svg)`}
            height="120px"
            width="120px"
          />
          <div className="empty-message tcenter grid mw-300 font-weight-500">
            {!emptyStateData?.noTitle && (
              <FormattedMessage
                id={
                  emptyStateData.title ||
                  'ticket.details.sideConversation.emptyState'
                }
              />
            )}
            {!emptyStateData?.noDescription && (
              <Description className="empty-description">
                <FormattedMessage
                  id={
                    emptyStateData.description ||
                    'ticket.details.sideConversation.emptyState.description'
                  }
                />
              </Description>
            )}
          </div>
          {createNewSideConversation && !readOnly && (
            <>
              {module === 'TICKET' ? (
                <Button
                  primary
                  icon={<SVGIcon color={COLOR_MAP.white} name="Helpcenter" />}
                  onClick={() => {
                    EventEmitter.dispatch('openTicket.side.conversation');
                  }}
                  isDisabled={isNewSideConversationDisabled}
                  loading={props?.newSideConversationLoading}
                >
                  <FormattedMessage id="app.sideConversation.create.new" />
                </Button>
              ) : (
                <NewSideConversation
                  btnText={emptyStateData.btnText}
                  key="NewSideConversationBtn"
                  module={module}
                  workItemId={workItemId}
                  updateCache={updateFeedsCache}
                  onSubmit={RefetchConversation}
                  showOriginalMsg={props.showOriginalMsg}
                  backgroundThreadConversation={backgroundThreadConversation}
                  templateId={templateId}
                  zIndex={module === 'PROJECT' ? 100 : 520}
                />
              )}
            </>
          )}
        </div>
      </EmptyState>
    </StyledConversation>
  );
};

export default injectIntl(withErrorBoundary(VendorConversations));
